import {Component, ElementRef, HostListener, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {HttpErrorResponse} from "@angular/common/http";
import {Subject} from "rxjs";
import {finalize, takeUntil} from "rxjs/operators";
import {AuthService} from "../../services/auth.service";
import {AssistantService} from "../../services/assistant.service";
import {AssistantActionsService} from "../../services/assistant-actions.service";
import {AssistantMessage, AssistantReply, AssistantSuggestion, AssistantTextBlock} from "../../interfaces/assistant";
import {HTTP_CODES} from "../../interfaces/http-codes";
import {assistantTextBlocks} from "../assistant-text";

/** Коди відповіді, після яких сесії вже немає: перехоплювач сам веде на вхід чи службову сторінку */
const SESSION_END_ERRORS = ['not_authorized', 'user_deleted', 'site_blocked'];

/** Довше бек обрізає (assistant/message) */
const MESSAGE_MAX_LENGTH = 1000;

/** Найбільша висота поля вводу, px - далі воно прокручується */
const INPUT_MAX_HEIGHT = 120;

/** Клас body, поки відкрита панель справа: сторінка звільняє під неї місце (відступ - styles.scss, картка #981) */
const PAGE_INSET_CLASS = 'assistant-panel-open';

/**
 * Повідомлення у вікні. Відповідь помічника - блоками (абзаци, списки, жирне, посилання - картка #986); текст клієнта
 * й повідомлення про збій - blocks: null, як є
 */
interface ChatMessage extends AssistantMessage {
  blocks: AssistantTextBlock[] | null;
}

/**
 * Картка #963: ІІ-помічник кабінету - крапля справа внизу і мінічат.
 *
 * Лежить у корені застосунку (app.component.html), тож вікно й розмова переживають переходи між
 * сторінками. Чи показувати - питаємо бек один раз за завантаження кабінету (assistant/status), лише
 * у залогіненого; enabled: false - нічого не малюємо. Відповідь - текст (посилання на сторінки кабінету клікабельні)
 * і команди вікну, які виконуються одразу (AssistantActionsService). Картка #986: легку розмітку відповіді (абзаци,
 * списки, жирне) розбирає assistant-text.ts, а малює шаблон - HTML з тексту моделі не буває. Картка #966:
 * кнопки-уточнення (команда suggest) - під останньою відповіддю; натиснута кнопка надсилає свій текст, як клієнт.
 * Картка #980: кнопка в шапці перемикає мінічат у вузьку панель справа на всю висоту і назад; вибір пам'ятаємо
 * (localStorage) - наступного разу вікно відкривається так само. Картка #981: відкрита панель не перекриває
 * сторінку, а стискає її вліво - клас PAGE_INSET_CLASS на body, відступ задає styles.scss (лише на широкому екрані).
 * Картка #982: незакрите вікно (localStorage) після перезавантаження відкривається саме - лише коли помічник
 * доступний (enabled: true); закриття, кінець сесії й вихід цей прапор знімають.
 */
@Component({
  selector: 'app-assistant-widget',
  templateUrl: './assistant-widget.component.html',
  styleUrls: ['./assistant-widget.component.scss']
})
export class AssistantWidgetComponent implements OnInit, OnDestroy {
  @ViewChild('dropButton') dropButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('feed') feed?: ElementRef<HTMLElement>;
  @ViewChild('input') input?: ElementRef<HTMLTextAreaElement>;

  private auth = inject(AuthService);
  private assistant = inject(AssistantService);
  private actions = inject(AssistantActionsService);

  private isDestroyed: Subject<boolean> = new Subject();
  private destroyed = false;

  /** Помічник доступний цьому користувачу (assistant/status) */
  public enabled = false;
  public isOpen = false;
  /** Вікно - вузька панель справа на всю висоту, а не мінічат над краплею (картка #980) */
  public isPanel = false;
  public isSending = false;
  public isResetting = false;
  public draft = '';
  public messages: ChatMessage[] = [];
  public readonly maxLength = MESSAGE_MAX_LENGTH;

  ngOnInit() {
    // Гість: історії попереднього входу у вкладці не лишаємо
    if (!this.auth.isLoggedIn()) {
      this.assistant.forget();
      return;
    }

    this.assistant.status()
      .pipe(takeUntil(this.isDestroyed))
      .subscribe({
        next: (status) => {
          this.enabled = !!status && status.enabled === true;

          if (this.enabled) {
            this.isPanel = this.assistant.loadPanelMode();
            this.messages = this.assistant.loadHistory().map((message) => this.view(message));

            // Картка #982: вікно лишили відкритим - відкриваємо одразу, але фокус у сторінки не забираємо
            if (this.assistant.loadOpenState()) {
              this.show();
            }
          } else {
            this.assistant.forget();
          }
        },
        // Статус не отримано - помічника не показуємо; про збій уже повідомив перехоплювач
        error: () => {},
      });
  }

  get canSend(): boolean {
    return this.enabled && !this.isSending && !this.isResetting && this.draft.trim() !== '';
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.assistant.saveOpenState(true);
    this.show();
    this.focusInput();
  }

  close() {
    this.isOpen = false;
    this.assistant.saveOpenState(false);
    this.syncPageInset();

    setTimeout(() => this.dropButton?.nativeElement.focus());
  }

  /** Мінічат <-> панель справа; вибір запам'ятовуємо */
  togglePanel() {
    this.isPanel = !this.isPanel;
    this.assistant.savePanelMode(this.isPanel);
    this.syncPageInset();
    this.scrollDown();
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.isOpen) {
      this.close();
    }
  }

  /** Enter - надіслати, Shift+Enter - новий рядок */
  onKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) {
      return;
    }

    event.preventDefault();
    this.send();
  }

  /** Поле вводу росте з текстом до INPUT_MAX_HEIGHT */
  autosize() {
    const input = this.input?.nativeElement;

    if (!input) {
      return;
    }

    // box-sizing: border-box - до висоти тексту (scrollHeight) додаємо рамку
    const border = input.offsetHeight - input.clientHeight;

    input.style.height = 'auto';
    input.style.height = Math.min(input.scrollHeight + border, INPUT_MAX_HEIGHT) + 'px';
  }

  /** Надіслати питання з поля вводу або (choice) текст натиснутої кнопки-уточнення - тоді набране в полі лишається */
  send(choice?: AssistantSuggestion) {
    const raw = choice ? choice.send : this.draft;

    if (!this.enabled || this.isSending || this.isResetting || raw.trim() === '') {
      return;
    }

    const text = raw.trim().slice(0, MESSAGE_MAX_LENGTH);
    // Розмова на екрані порожня (нова вкладка, очищено) - модель теж починає з нуля, а не з того,
    // що пам'ятає бек з іншої вкладки
    const reset = this.messages.length === 0;
    let answered = false;

    if (!choice) {
      this.draft = '';
      setTimeout(() => this.autosize());
    }

    this.push({role: 'user', text});
    this.isSending = true;

    this.assistant.message(text, reset)
      .pipe(
        takeUntil(this.isDestroyed),
        finalize(() => this.isSending = false)
      )
      .subscribe({
        next: (reply) => {
          answered = true;
          this.onReply(reply);
        },
        error: (error) => {
          answered = true;
          this.onError(error, 'Відповіді немає');
        },
        complete: () => {
          // Без відповіді запит завершує лише кнопка «Скасувати» лоадера (знімає всі запити)
          if (!answered && !this.destroyed) {
            this.push({role: 'assistant', text: 'Запит скасовано. Надішліть питання ще раз.', error: true});
          }
        },
      });
  }

  /** Почати розмову заново: бек забуває її (assistant/reset), вікно - теж */
  clear() {
    if (!this.enabled || this.isSending || this.isResetting) {
      return;
    }

    this.isResetting = true;

    this.assistant.reset()
      .pipe(
        takeUntil(this.isDestroyed),
        finalize(() => this.isResetting = false)
      )
      .subscribe({
        next: (reply) => {
          if (reply && typeof reply.error === 'string' && SESSION_END_ERRORS.includes(reply.error)) {
            this.shutdown();
            return;
          }

          this.messages = [];
          this.assistant.forget();
          this.focusInput();
        },
        error: (error) => this.onError(error, 'Розмову не очищено'),
      });
  }

  trackByIndex(index: number) {
    return index;
  }

  /** Кнопки - лише під останнім повідомленням, якщо це відповідь помічника */
  showOptions(message: ChatMessage, isLast: boolean): boolean {
    return isLast && message.role === 'assistant' && !message.error && !!message.options && message.options.length > 0;
  }

  private onReply(reply: AssistantReply) {
    if (reply && typeof reply.error === 'string' && SESSION_END_ERRORS.includes(reply.error)) {
      this.shutdown();
      return;
    }

    const text = reply && typeof reply.text === 'string' ? reply.text.trim() : '';

    if (text === '') {
      this.push({role: 'assistant', text: 'Помічник не дав відповіді. Спробуйте запитати інакше.', error: true});
    } else {
      const options = this.actions.suggestions(reply?.actions);

      this.push(options.length ? {role: 'assistant', text, options} : {role: 'assistant', text});
    }

    this.actions.run(reply?.actions);
  }

  /**
   * Збій запиту. 403 і 429 несуть у тілі text для вікна (загальний тост для них перехоплювач не показує);
   * решта - своя причина й що робити. what - що не вдалося («Відповіді немає», «Розмову не очищено»).
   */
  private onError(error: unknown, what: string) {
    const response = error instanceof HttpErrorResponse ? error : null;
    const status = response ? response.status : null;
    const bodyText = response && response.error && typeof response.error.text === 'string' ? response.error.text.trim() : '';

    if (status === HTTP_CODES.FORBIDDEN) {
      this.revoke(bodyText || 'Помічник вам недоступний.');
      return;
    }

    if (status === HTTP_CODES.TOO_MANY_REQUESTS) {
      this.push({role: 'assistant', text: bodyText || 'Забагато запитань за годину. Спробуйте трохи пізніше.', error: true});
      return;
    }

    let cause = 'запит відхилено. Оновіть сторінку й спробуйте ще раз.';

    if (status === HTTP_CODES.NO_RESPONSE) {
      cause = 'немає зв\'язку з сервером. Перевірте інтернет і спробуйте ще раз.';
    } else if (status !== null && status >= HTTP_CODES.SERVER_ERROR) {
      cause = 'сервер не відповів. Спробуйте ще раз за хвилину.';
    }

    this.push({role: 'assistant', text: what + ': ' + cause, error: true});
  }

  /** Доступ до помічника забрали (403): історію стираємо, поле вводу гасимо, після закриття краплі не буде */
  private revoke(text: string) {
    this.enabled = false;
    this.assistant.forget();
    this.messages = [...this.messages, this.view({role: 'assistant', text, error: true})];
    this.scrollDown();
  }

  /** Сесії немає (вихід, видалення, сайт закрито): прибираємо помічника й історію вкладки */
  private shutdown() {
    this.enabled = false;
    this.isOpen = false;
    this.assistant.saveOpenState(false);
    this.syncPageInset();
    this.messages = [];
    this.assistant.forget();
  }

  private push(message: AssistantMessage) {
    this.messages = [...this.messages, this.view(message)];

    if (this.enabled) {
      this.assistant.saveHistory(this.messages);
    }

    this.scrollDown();
  }

  /**
   * Оформлення й посилання - лише у відповідях помічника (і в старих з історії вкладки: там той самий сирий текст);
   * текст клієнта і повідомлення про збої - як є
   */
  private view(message: AssistantMessage): ChatMessage {
    const blocks = message.role === 'assistant' && !message.error
      ? assistantTextBlocks(message.text, (text) => this.actions.textParts(text))
      : null;

    return {...message, blocks};
  }

  private scrollDown() {
    setTimeout(() => {
      const feed = this.feed?.nativeElement;

      if (feed) {
        feed.scrollTop = feed.scrollHeight;
      }
    });
  }

  /** Показати вікно в обраному вигляді, внизу розмови; фокус дає лише open() - клік по краплі */
  private show() {
    this.isOpen = true;
    this.syncPageInset();
    this.scrollDown();
  }

  private focusInput() {
    setTimeout(() => this.input?.nativeElement.focus());
  }

  /** Відкрита панель - сторінка стискається вліво; мінічат і закрите вікно сторінку не зсувають */
  private syncPageInset() {
    document.body.classList.toggle(PAGE_INSET_CLASS, this.isOpen && this.isPanel);
  }

  ngOnDestroy() {
    document.body.classList.remove(PAGE_INSET_CLASS);
    this.destroyed = true;
    this.isDestroyed.next(true);
    this.isDestroyed.complete();
  }
}
